import { Box, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { headerHeight, headerHeightSmall } from 'components/common/navbar/navbar';
import { useAppState } from 'lib/app-state';

/**
 * Goes on the card that holds a `StickyCardHeader`. `hidden` would make the
 * card the header's scroll container, which never scrolls, so the header would
 * never stick. `clip` would cut the gap cover above the stuck header off at
 * the card's border.
 */
export const stickyCardSx = {
  overflow: 'visible'
} as const;
interface Props {
  /**
   * In the mini cart, which scrolls a box of its own: the header stops at the
   * top of that box instead of under the navbar.
   */
  inAside?: boolean;
  /** Off, the header stays in the flow like any other content. */
  enabled?: boolean;
  /** The card's border, which the stuck header draws its own top with. */
  frameBorderWidth?: number;
  frameBorderColor?: string;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

/**
 * A card's header that stays pinned while the rest of the card scrolls past
 * it: under the navbar on the page, or at the top of the mini cart's scrolling
 * box. Once stuck it draws a rounded, bordered card top of its own and covers
 * the gap above it.
 *
 * Renders as direct children of the card (a `position: relative` card with
 * `stickyCardSx`) and reaches the card's edges, so the stuck frame lines up
 * with the card's own border.
 */
export const StickyCardHeader = ({
  inAside = false,
  enabled = true,
  frameBorderWidth = 1,
  frameBorderColor = 'divider',
  sx,
  children
}: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    navbarExtraHeight,
    navbarBannerHeight
  } = useAppState();

  // On the page the header stops a little below the navbar rather than flush
  // against it. The mini cart scrolls a box of its own that starts below its
  // title, so there the header stops at the top of that box.
  const stickyGap = inAside ? 0 : parseFloat(theme.spacing(isMobile ? 2 : 3));
  const stickyTop = inAside ? 0 : (isMobile ? headerHeightSmall : headerHeight) + navbarExtraHeight + navbarBannerHeight + stickyGap;

  // A zero-height marker at the card's top: once it has passed the line the
  // header stops at, the header is stuck and gets edges of its own.
  const stuckSentinelRef = useRef<HTMLDivElement | null>(null);
  const [isStuck, setIsStuck] = useState(false);
  useEffect(() => {
    const sentinel = stuckSentinelRef.current;
    if (!enabled || !sentinel) {
      setIsStuck(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      // The root's bounds already have the margin taken off the top.
      const line = entry.rootBounds?.top ?? stickyTop;
      setIsStuck(!entry.isIntersecting && entry.boundingClientRect.top < line);
    }, {
      root: scrollParent(sentinel),
      rootMargin: `-${stickyTop}px 0px 0px 0px`
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, stickyTop]);
  if (!enabled) return <Box sx={sx}>{children}</Box>;
  return <>
      {/* Out of the flow, so it adds no gap in a card that spaces its
          children with one. */}
      <Box ref={stuckSentinelRef} aria-hidden sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0
    }} />
      <Box sx={[{
      position: 'sticky',
      top: stickyTop,
      zIndex: 1,
      bgcolor: 'background.paper',
      // The card doesn't clip, so the header rounds off with it.
      borderTopLeftRadius: 'inherit',
      borderTopRightRadius: 'inherit',
      // While stuck the header draws its own card top over the card's
      // sides. Both layers sit behind its content (the header is a
      // stacking context): `::before` paints the background around the
      // card over the gap above the header and its own area, hiding the
      // content and the card's side borders scrolling behind it;
      // `::after` paints the rounded, bordered top on it.
      ...(isStuck && {
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          zIndex: -1,
          bottom: 0,
          left: -frameBorderWidth,
          right: -frameBorderWidth
        },
        '&::before': {
          top: -stickyGap,
          // The mini cart drawer is a paper surface; the page is not.
          bgcolor: inAside ? 'background.paper' : 'background.default'
        },
        '&::after': {
          top: 0,
          bgcolor: 'background.paper',
          border: frameBorderWidth,
          borderColor: frameBorderColor,
          // The bottom edge parts the header from what scrolls under it,
          // so it stays a plain rule whatever the card's outline.
          borderBottom: 1,
          borderBottomColor: 'divider',
          borderRadius: 'inherit',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0
        }
      })
    }, ...(Array.isArray(sx) ? sx : [sx])]}>
        {children}
      </Box>
    </>;
};

/** The nearest ancestor that scrolls, or `null` when the window does. */
const scrollParent = (element: HTMLElement): HTMLElement | null => {
  // The global styles set `overflow-y: scroll` on <html>, but that scrolls
  // the viewport, which only a null root measures against.
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(node).overflowY)) return node;
  }
  return null;
};